// NodeMissionHeader.jsx

import React, { useCallback, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  NODE_MISSION_STATUS_LABELS,
  getMissionStatusBadgeTheme,
} from 'node/missions/mission/utils/missionRunUtils';
import { getPlaybookSwitcherMeta } from 'node/missions/mission/utils/playbookRunSelectionUtils';

// CSS
import './NodeMissionHeader.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Popup from 'components/popup/Popup';

/**
 * NodeMissionHeader
 *
 * Displays the header for a live playbook, including an optional
 * title switcher when the type offers more than one playbook.
 *
 * @param {object} template - The Mission template.
 * @param {string} missionStatus - The status of the Mission.
 * @param {boolean} hasRun - Whether the Mission has been run.
 * @param {string} scheduleSummary - Optional projected or actual completion summary.
 * @param {function} [onOpenPlaybookFocus] - Opens the playbook wizard.
 * @param {function} [onShowPlaybookList] - Shows the full playbook step list.
 * @param {function} [onOpenWorkflowSettings] - Opens the workflow designer for this template.
 * @param {object[]|null} [playbookSwitcherEntries] - All templates for the title picker.
 * @param {string} [selectedTemplateKey] - Currently selected template key.
 * @param {function} [onSelectPlaybook] - Selects an existing run or starts an unstarted one.
 *
 * @returns {ReactNode} - NodeMissionHeader.
 */
const NodeMissionHeader = React.memo(({
  template,
  missionStatus,
  hasRun,
  scheduleSummary = '',
  onOpenPlaybookFocus = null,
  onShowPlaybookList = null,
  onOpenWorkflowSettings = null,
  playbookSwitcherEntries = null,
  selectedTemplateKey = '',
  onSelectPlaybook = null,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  // Title switcher popup open state
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // ----- REFS ----- //

  const titleAnchorRef = useRef(null);

  // ----- MEMOS ----- //

  const statusBadgeTheme = getMissionStatusBadgeTheme(theme, missionStatus);
  const showPlaybookFocus = typeof onOpenPlaybookFocus === 'function';
  const showPlaybookList = typeof onShowPlaybookList === 'function';
  const showSettings = typeof onOpenWorkflowSettings === 'function';
  const switcherEntries = useMemo(
    () => (Array.isArray(playbookSwitcherEntries) ? playbookSwitcherEntries : [])
      .filter((entry) => String(entry?.template?.key || '').trim()),
    [playbookSwitcherEntries]
  );
  const showPlaybookSwitcher = (
    switcherEntries.length >= 2
    && typeof onSelectPlaybook === 'function'
  );
  const resolvedSelectedKey = String(
    selectedTemplateKey || template?.key || ''
  ).trim();
  const titleLabel = String(template?.title || '').trim() || 'Untitled Playbook';

  // ----- HANDLERS ----- //

  /**
   * handleToggleSwitcher
   *
   * Opens or closes the playbook title picker.
   */
  const handleToggleSwitcher = useCallback(() => {
    if (!showPlaybookSwitcher) return;
    setSwitcherOpen((previous) => !previous);
  }, [showPlaybookSwitcher]);

  /**
   * handleSelectPlaybook
   *
   * Commits a picker row and closes the switcher.
   *
   * @param {string} templateKey - Template key from the picker.
   */
  const handleSelectPlaybook = useCallback((templateKey) => {
    const key = String(templateKey || '').trim();
    setSwitcherOpen(false);
    if (!key || typeof onSelectPlaybook !== 'function') return;
    onSelectPlaybook(key);
  }, [onSelectPlaybook]);

  // ----- RENDER ----- //

  return (

    <div className="node-mission-header-container">

      {/* CARD HEADER MAIN */}
      <div className="node-mission-header-main">

        {/* CARD TITLE ROW */}
        <div className="node-mission-header-title-row">

          <div className="node-mission-header-title-cluster">
            <Icon
              iconKey={String(template?.iconKey || '').trim() || 'account_tree'}
              width={26}
              height={26}
              color={theme.foregroundMuted}
            />

            {/* CARD TITLE */}
            {showPlaybookSwitcher ? (
              <button
                className="node-mission-header-title-button"
                ref={titleAnchorRef}
                title={titleLabel}
                type="button"
                onClick={handleToggleSwitcher}
              >
                <div className="node-mission-header-title">
                  {titleLabel}
                </div>
                <Icon
                  color={theme.foregroundMuted}
                  height={18}
                  iconKey="keyboard_arrow_down"
                  width={18}
                />
              </button>
            ) : (
              <div className="node-mission-header-title">
                {titleLabel}
              </div>
            )}

            {/* PLAYBOOK LIST */}
            {showPlaybookList ? (
              <button
                className="node-mission-header-icon-button"
                title="See all steps"
                type="button"
                onClick={onShowPlaybookList}
              >
                <Icon
                  color={theme.foregroundMuted}
                  height={16}
                  iconKey="format_list_bulleted"
                  width={16}
                />
              </button>
            ) : null}

            {/* PLAYBOOK WIZARD */}
            {showPlaybookFocus ? (
              <button
                className="node-mission-header-icon-button"
                title="Playbook wizard"
                type="button"
                onClick={onOpenPlaybookFocus}
              >
                <Icon
                  color={theme.foregroundMuted}
                  height={16}
                  iconKey="wand_stars"
                  width={16}
                />
              </button>
            ) : null}

            {/* WORKFLOW SETTINGS */}
            {showSettings ? (
              <button
                className="node-mission-header-icon-button"
                title="Design playbook"
                type="button"
                onClick={onOpenWorkflowSettings}
              >
                <Icon
                  color={theme.foregroundMuted}
                  height={16}
                  iconKey="settings"
                  width={16}
                />
              </button>
            ) : null}
          </div>

          {/* STATUS BADGE */}
          <div
            className="node-mission-header-status-badge"
            style={{
              backgroundColor: statusBadgeTheme.backgroundColor,
              color: statusBadgeTheme.color,
            }}
          >
            {hasRun ? NODE_MISSION_STATUS_LABELS[missionStatus] : 'Ready to start'}
          </div>

        </div>

        {/* SCHEDULE SUMMARY */}
        {scheduleSummary ? (
          <div className="node-mission-header-schedule-summary">
            {scheduleSummary}
          </div>
        ) : null}
      </div>

      {/* PLAYBOOK SWITCHER */}
      {showPlaybookSwitcher ? (
        <Popup
          align="start"
          anchorRef={titleAnchorRef}
          isOpen={switcherOpen}
          placement="bottom"
          setOpen={setSwitcherOpen}
        >
          <div className="node-mission-header-switcher">
            {switcherEntries.map((entry) => {
              const templateKey = String(entry?.template?.key || '').trim();
              const entryTitle = String(entry?.template?.title || '').trim() || 'Untitled Playbook';
              const entryIconKey = String(entry?.template?.iconKey || '').trim() || 'account_tree';
              const isSelected = templateKey === resolvedSelectedKey;
              const meta = getPlaybookSwitcherMeta(entry);

              return (
                <button
                  key={templateKey}
                  className={`node-mission-header-switcher-item${isSelected ? ' is-selected' : ''}`}
                  type="button"
                  onClick={() => handleSelectPlaybook(templateKey)}
                >
                  <div
                    className="node-mission-header-switcher-item-icon"
                    style={{ backgroundColor: theme.backgroundMuted }}
                  >
                    <Icon
                      color={theme.foreground}
                      height={18}
                      iconKey={entryIconKey}
                      width={18}
                    />
                  </div>

                  <div className="node-mission-header-switcher-item-copy">
                    <div className="node-mission-header-switcher-item-title">
                      {entryTitle}
                    </div>
                    <div
                      className="node-mission-header-switcher-item-meta"
                      style={{ color: theme.foregroundMuted }}
                    >
                      {meta}
                    </div>
                  </div>

                  {isSelected ? (
                    <Icon
                      color={theme.foreground}
                      height={16}
                      iconKey="check"
                      width={16}
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </Popup>
      ) : null}

    </div>
  );

});

// Dev Tools
NodeMissionHeader.displayName = 'NodeMissionHeader';

export default NodeMissionHeader;
